import { useRuntimeConfig } from '#imports'

import type { GetShippingMethodsMetadata } from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants'
import type {
  DeliveryMethod,
  MarketWithSubMarkets,
  ShippingRulesGroup,
  SubMarket,
} from '@backmarket/http-api/src/api-specs-shipping/rules'
import type { I18n } from '@backmarket/nuxt-module-i18n/types'

import { MAX_CATEGORIES_DISPLAYED, NO_CURRENCY } from './CategoryList.constants'
import translations from './CategoryList.translations'
import type {
  PotentialCurrency,
  ShippingCategoryGroupMetadata,
  ShippingCategoryGroupRules,
  ShippingCategoryGroups,
  ShippingMethodsMetadata,
} from './CategoryList.types'

// TODO https://imperatorz.slack.com/archives/C544PFLFL/p1756305968322459
// eslint-disable-next-line @backmarket/custom-rules/no-root-scope-composable
const { FF_ENABLE_NEW_SHIPPING_BACKSHIP_CARRIERS } = useRuntimeConfig().public

export function transformShippingMethodsMetadata(
  shippingMethodsMetadata: GetShippingMethodsMetadata,
): ShippingMethodsMetadata {
  return {
    ...shippingMethodsMetadata,
    providerOptions: shippingMethodsMetadata.carriers.reduce(
      (arr, carrier) => {
        const shouldAddCarrier =
          (!FF_ENABLE_NEW_SHIPPING_BACKSHIP_CARRIERS && !carrier.backship) ||
          FF_ENABLE_NEW_SHIPPING_BACKSHIP_CARRIERS

        if (shouldAddCarrier) {
          arr.push({
            value: carrier.codename,
            label: carrier.name,
            carrier,
          })
        }

        return arr
      },
      [] as ShippingMethodsMetadata['providerOptions'],
    ),
    shippingCategoryGroups: shippingMethodsMetadata.shippingCategoryGroups.map(
      (categoryGroup) => ({
        ...categoryGroup,
        description: categoryGroup.shippingCategories
          .slice(0, MAX_CATEGORIES_DISPLAYED)
          .map(({ name }) => name)
          .join(' / '),
        countMore: Math.max(
          0,
          categoryGroup.shippingCategories.length - MAX_CATEGORIES_DISPLAYED,
        ),
      }),
    ),
  }
}

function formatMarketsToMarketWithSubMarkets(
  markets: DeliveryMethod['markets'],
  marketsWithSubMarkets: MarketWithSubMarkets[],
): MarketWithSubMarkets[] {
  return markets.map(({ market, subMarkets }) => {
    const associatedMarketWithSubMarkets = marketsWithSubMarkets.find(
      ({ market: marketFromMarketsWithSubMarkets }) =>
        marketFromMarketsWithSubMarkets === market,
    )

    return {
      market,
      subMarkets: subMarkets.map((subMarketCode) => {
        return (associatedMarketWithSubMarkets?.subMarkets.find(
          ({
            subMarketCode: subMarketCodeFromAssociatedMarketWithSubMarkets,
          }) =>
            subMarketCodeFromAssociatedMarketWithSubMarkets === subMarketCode,
        ) || []) as SubMarket[]
      }),
    } as unknown as MarketWithSubMarkets
  })
}

export function transformShippingRules(
  shippingCategoryGroups: ShippingRulesGroup[],
  marketsWithSubMarkets: MarketWithSubMarkets[],
): ShippingCategoryGroups {
  return (shippingCategoryGroups || []).reduce((obj, categoryGroup) => {
    let markets: MarketWithSubMarkets[] = []
    let categories: ShippingCategoryGroupRules['categories'] = []

    const categoryGroupRules: ShippingCategoryGroupRules = {
      rules: categoryGroup.shippingRules.map((rule) => {
        categories = [...categories, ...rule.shippingCategoryIds]

        return {
          id: rule.id,
          categories: rule.shippingCategoryIds,
          preparationTime: rule.fulfilmentTimeHours,
          freeMethods: rule.deliveryModes
            .filter(
              ({ deliveryMode }) =>
                deliveryMode === 'HOME_DELIVERY_FREE' ||
                deliveryMode === 'COLLECTION_POINT_DELIVERY_FREE',
            )
            .flatMap((deliveryMode) =>
              deliveryMode.deliveryMethods.map((method) => {
                const formattedMarkets = formatMarketsToMarketWithSubMarkets(
                  method.markets,
                  marketsWithSubMarkets,
                )

                markets = [...markets, ...formattedMarkets]

                return {
                  id: method.id,
                  markets: formattedMarkets,
                  shippingProvider: method.carrierCodename,
                  shippingTime: method.transitTime,
                  price: 0,
                  currency: NO_CURRENCY,
                }
              }),
            ),
          paidMethods: rule.deliveryModes
            .filter(({ deliveryMode }) => deliveryMode === 'HOME_DELIVERY_PAID')
            .flatMap((deliveryMode) =>
              deliveryMode.deliveryMethods.map((method) => {
                const formattedMarkets = formatMarketsToMarketWithSubMarkets(
                  method.markets,
                  marketsWithSubMarkets,
                )

                markets = [...markets, ...formattedMarkets]

                return {
                  id: method.id,
                  markets: formattedMarkets,
                  shippingProvider: method.carrierCodename,
                  shippingTime: method.transitTime,
                  price: +(method.shippingFee?.amount || 0),
                  currency: method.shippingFee?.currency || NO_CURRENCY,
                }
              }),
            ),
        }
      }),
      markets,
      categories,
    }

    return {
      ...obj,
      [categoryGroup.id]: categoryGroupRules,
    }
  }, {} as ShippingCategoryGroups)
}

export function sortMarkets({ markets }: { markets: MarketWithSubMarkets[] }) {
  return markets?.sort((a, b) => a.market.localeCompare(b.market))
}

export function getShippingProviderLabel(
  { shippingMethods }: ShippingCategoryGroupMetadata,
  { shippingProvider }: { shippingProvider: string },
) {
  const provider = shippingMethods.providerOptions.find(
    ({ value }) => value === shippingProvider,
  )

  return provider?.label || ''
}

export function getShippingTimeLabel(
  { shippingTime }: { shippingTime: string | number },
  getFormatHoursInDay: (hours: number) => string,
) {
  return getFormatHoursInDay(Number(shippingTime))
}

export function getPreparationTimeLabel(
  { preparationTime }: ShippingCategoryGroupMetadata,
  getFormatHoursInDay: (hours: number) => string,
) {
  return getFormatHoursInDay(preparationTime)
}

export function getTotalTimeLabel(
  { preparationTime }: ShippingCategoryGroupMetadata,
  { shippingTime }: { shippingTime: string | number },
  getFormatHoursInDay: (hours: number) => string,
) {
  return getFormatHoursInDay(preparationTime + +shippingTime)
}

export function getPriceLabel(
  i18n: I18n,
  isFree: boolean,
  { price, currency }: { price: number; currency: PotentialCurrency },
) {
  return isFree || currency === NO_CURRENCY
    ? i18n(translations.free)
    : i18n.price({ amount: price.toString(), currency })
}

export function getCarrier(
  providerOptions: ShippingMethodsMetadata['providerOptions'],
  codeName: string,
) {
  return providerOptions.find(({ carrier }) => carrier.codename === codeName)
    ?.carrier
}
